import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ADMIN_API } from '../../common/common'
import { getImageUrl, Logout } from '../../common/Funcation'
import { styleObjectBlackButton } from '../../common/StyleSeter'
import { getApi, notifyError, notifyMessage, postApi } from '../../service'
import { WebSiteList } from './interface'

type MaintenanceKind = 'frontend' | 'ag'
type BulkScope = 'frontend' | 'ag' | 'both'

function toDatetimeLocalValue(date: Date) {
  const d = date instanceof Date ? date : new Date(date)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultEndTime() {
  return toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
}

function isOn(item: WebSiteList, kind: MaintenanceKind) {
  return kind === 'ag' ? !!item?.isAgMaintenance : !!item?.isMaintenance
}

function endAtValue(item: WebSiteList, kind: MaintenanceKind) {
  return kind === 'ag' ? item?.agMaintenanceEndAt : item?.maintenanceEndAt
}

function ManageWebsites() {
  const dispatch = useDispatch();

  const DD = useSelector((e: any) => e.domainDetails);
  const HeaderData = useSelector((e: any) => e.Header);
  const [headerOptions, setHeaderOptions] = useState(HeaderData);
  const [isHover, setIsHover] = useState(false);
  const [hoverId, setHoverId] = useState({ isHover: "" });
  const handleMouseEnter = (id: string = "") => {
    if (id) {
      setHoverId({ isHover: id });
    } else {
      setIsHover(true);
    }
  };
  const handleMouseLeave = (id: string = "") => {
    if (id) {
      setHoverId({ isHover: "" });
    } else {
      setIsHover(false);
    }
  };
  const navigate = useNavigate()
  const [pageData, setPageData] = useState<any>({})
  const [saving, setSaving] = useState(false)
  const [maintenanceModal, setMaintenanceModal] = useState<{
    open: boolean
    site: WebSiteList | null
    endAt: string
    kind: MaintenanceKind
  }>({ open: false, site: null, endAt: defaultEndTime(), kind: 'frontend' })
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [bulkScope, setBulkScope] = useState<BulkScope>('both')
  const [bulkEndAt, setBulkEndAt] = useState(defaultEndTime)
  const selectAllRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getPageData("All", "1");
    return () => {};
  }, []);

  const getPageData = async (TYPE: string, PAGE: string) => {
    let data = {
      api: ADMIN_API.SETTING.WEBSITE.LIST,
    }

    await getApi(data).then(function (response) {
      console.log(response);
      setPageData(response.data.data)

    }).catch(err => {
      if (err.response.data.statusCode === 401) {
        Logout()
        navigate('/login')
      }
    })

  }

  const closeMaintenanceModal = () => {
    setMaintenanceModal({ open: false, site: null, endAt: defaultEndTime(), kind: 'frontend' })
  }

  const openMaintenanceModal = (item: WebSiteList, kind: MaintenanceKind = 'frontend') => {
    const existing = endAtValue(item, kind) ? new Date(endAtValue(item, kind) as any) : null
    const endAt = existing && existing.getTime() > Date.now()
      ? toDatetimeLocalValue(existing)
      : defaultEndTime()
    setMaintenanceModal({ open: true, site: item, endAt, kind })
  }

  const handleMaintenanceToggle = (item: WebSiteList, kind: MaintenanceKind = 'frontend') => {
    if (isOn(item, kind)) {
      updateMaintenance(item._id, false, undefined, kind)
      return
    }
    openMaintenanceModal(item, kind)
  }

  const updateMaintenance = async (
    ID: string,
    enabled: boolean,
    endAt?: string,
    kind: MaintenanceKind = 'frontend'
  ) => {
    if (saving) return
    setSaving(true)
    const isAg = kind === 'ag'
    let data = {
      api: ADMIN_API.SETTING.WEBSITE.MAINTENANCE,
      value: isAg
        ? {
            id: ID,
            isAgMaintenance: enabled,
            ...(enabled && endAt
              ? { agMaintenanceEndAt: new Date(endAt).toISOString() }
              : {}),
          }
        : {
            id: ID,
            isMaintenance: enabled,
            ...(enabled && endAt
              ? { maintenanceEndAt: new Date(endAt).toISOString() }
              : {}),
          },
    };

    const onLabel = isAg ? 'AG maintenance enabled' : 'Maintenance enabled'
    const offLabel = isAg ? 'AG maintenance turned off' : 'Maintenance turned off'

    await postApi(data).then(function (response) {
      notifyMessage(response?.data?.message || (enabled ? onLabel : offLabel))
      if (DD?._id === ID) {
        dispatch({
          type: "DOMAIN_DETAILS",
          payload: {
            ...DD,
            ...(isAg
              ? {
                  isAgMaintenance: enabled,
                  agMaintenanceEndAt: enabled && endAt ? new Date(endAt).toISOString() : null,
                }
              : {
                  isMaintenance: enabled,
                  maintenanceEndAt: enabled && endAt ? new Date(endAt).toISOString() : null,
                }),
          },
        })
      }
      closeMaintenanceModal()
      getPageData('All', '1')
    }).catch(err => {
      if (err.response?.data?.statusCode === 401) {
        Logout()
        navigate('/login')
      } else {
        notifyError(err.response?.data?.message || 'Failed to update maintenance')
      }
    }).finally(() => {
      setSaving(false)
    })
  }

  const confirmMaintenance = () => {
    if (!maintenanceModal.site) return
    if (!maintenanceModal.endAt) {
      notifyError('Please set a maintenance end time.')
      return
    }
    if (new Date(maintenanceModal.endAt).getTime() <= Date.now()) {
      notifyError('Maintenance end time must be in the future.')
      return
    }
    updateMaintenance(maintenanceModal.site._id, true, maintenanceModal.endAt, maintenanceModal.kind)
  }

  const filteredWebsites = useMemo(() => {
    const websites: WebSiteList[] = Array.isArray(pageData) ? pageData : []
    const query = search.trim().toLowerCase()
    if (!query) return websites
    return websites.filter((item) => {
      const text = [item.title, item.domain, ...(item.alternativeDomains || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return text.includes(query)
    })
  }, [pageData, search])

  const filteredIds = filteredWebsites.map((item) => item._id)
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id))
  const someFilteredSelected = filteredIds.some((id) => selectedIds.includes(id))

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected
    }
  }, [someFilteredSelected, allFilteredSelected])

  const toggleRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      if (allFilteredSelected) {
        const hidden = new Set(filteredIds)
        return prev.filter((id) => !hidden.has(id))
      }
      const next = prev.slice()
      filteredIds.forEach((id) => {
        if (!next.includes(id)) next.push(id)
      })
      return next
    })
  }

  const applyBulkMaintenance = async (enabled: boolean) => {
    if (!selectedIds.length) {
      notifyError('Select at least one website.')
      return
    }
    if (enabled && !bulkEndAt) {
      notifyError('Please set a maintenance end time.')
      return
    }
    if (enabled && new Date(bulkEndAt).getTime() <= Date.now()) {
      notifyError('Maintenance end time must be in the future.')
      return
    }
    if (saving) return

    setSaving(true)
    const isFrontend = bulkScope === 'frontend' || bulkScope === 'both'
    const isAg = bulkScope === 'ag' || bulkScope === 'both'
    const endIso = enabled ? new Date(bulkEndAt).toISOString() : null
    const ids = selectedIds.slice()
    const failedIds: string[] = []
    let firstError = ''
    let unauthorized = false
    const queue = ids.slice()

    const updateOneSite = async (id: string) => {
      const value: any = { id }
      if (isFrontend) {
        value.isMaintenance = enabled
        if (enabled) value.maintenanceEndAt = endIso
      }
      if (isAg) {
        value.isAgMaintenance = enabled
        if (enabled) value.agMaintenanceEndAt = endIso
      }
      await postApi({
        api: ADMIN_API.SETTING.WEBSITE.MAINTENANCE,
        value,
      })
    }

    const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length && !unauthorized) {
        const id = queue.shift()
        if (!id) return
        try {
          await updateOneSite(id)
        } catch (err: any) {
          if (err?.response?.data?.statusCode === 401) {
            unauthorized = true
            return
          }
          failedIds.push(id)
          if (!firstError) {
            firstError = err?.response?.data?.message || 'Failed to update maintenance'
          }
        }
      }
    })

    await Promise.all(workers)

    if (unauthorized) {
      setSaving(false)
      Logout()
      navigate('/login')
      return
    }

    const updated = ids.length - failedIds.length
    if (updated > 0 && DD?._id && ids.includes(DD._id) && !failedIds.includes(DD._id)) {
      dispatch({
        type: 'DOMAIN_DETAILS',
        payload: {
          ...DD,
          ...(isFrontend
            ? { isMaintenance: enabled, maintenanceEndAt: endIso }
            : {}),
          ...(isAg
            ? { isAgMaintenance: enabled, agMaintenanceEndAt: endIso }
            : {}),
        },
      })
    }

    if (updated === 0) {
      notifyError(firstError || 'Failed to update maintenance')
    } else if (failedIds.length) {
      notifyError(`${updated} updated, ${failedIds.length} failed. ${firstError}`)
      setSelectedIds(failedIds)
      getPageData('All', '1')
    } else {
      const scopeLabel = bulkScope === 'both' ? 'Frontend and backend' : isAg ? 'Backend' : 'Frontend'
      notifyMessage(
        enabled
          ? `${scopeLabel} maintenance started for ${updated} website(s).`
          : `${scopeLabel} maintenance turned off for ${updated} website(s).`
      )
      setSelectedIds([])
      getPageData('All', '1')
    }
    setSaving(false)
  }

  const editClick = (e: any) => {
    dispatch({ type: "EDIT_WEBSITE", payload: e });
    navigate("/edit-website/" + e._id);
  };

  const renderToggleCell = (item: WebSiteList, kind: MaintenanceKind) => {
    const active = isOn(item, kind)
    const endAt = endAtValue(item, kind)
    const idPrefix = kind === 'ag' ? 'ag-maintenance' : 'maintenance'
    return (
      <td style={{ width: "14%" }}>
        <div className="form-check form-switch" style={{ height: "25px" }}>
          <input
            onClick={() => handleMaintenanceToggle(item, kind)}
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={active}
            id={`${idPrefix}-${item._id}`}
            onChange={() => {}}
          />
          <label className="form-check-label" htmlFor={`${idPrefix}-${item._id}`} />
        </div>
        {active && endAt ? (
          <div className="maintenance-end-meta">
            Ends {moment(endAt).format('DD MMM YYYY HH:mm')}
            <button type="button" className="maintenance-end-change" onClick={() => openMaintenanceModal(item, kind)}>Change</button>
          </div>
        ) : active ? (
          <div className="maintenance-end-meta">
            No end time
            <button type="button" className="maintenance-end-change" onClick={() => openMaintenanceModal(item, kind)}>Set time</button>
          </div>
        ) : null}
      </td>
    )
  }

  const modalIsAg = maintenanceModal.kind === 'ag'
  const modalActive = maintenanceModal.site ? isOn(maintenanceModal.site, maintenanceModal.kind) : false

  return (
    <div className="container settings full-wrap website-list">
      <div className="top_header">
        <div className="top_header_title mt-3 d_flex d_flex_justify_spacebt">
          <h5>Manage Websites</h5>
          {headerOptions?.name === "O" &&
            <a href="/add-website" onMouseEnter={() => handleMouseEnter()} onMouseLeave={() => handleMouseLeave()} style={styleObjectBlackButton(DD?.colorSchema, isHover)} className="btn_black d_flex d_flex_align_center">Add New</a>
          }
        </div>
      </div>
      <div className="website-list-tools">
        <input
          type="search"
          className="form-control website-list-search"
          placeholder="Search title or domain"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="website-list-bulk">
          <span className="website-list-count">{selectedIds.length} selected</span>
          <label>
            <input
              type="radio"
              name="bulkScope"
              value="frontend"
              checked={bulkScope === 'frontend'}
              onChange={() => setBulkScope('frontend')}
            />
            Frontend
          </label>
          <label>
            <input
              type="radio"
              name="bulkScope"
              value="ag"
              checked={bulkScope === 'ag'}
              onChange={() => setBulkScope('ag')}
            />
            Backend
          </label>
          <label>
            <input
              type="radio"
              name="bulkScope"
              value="both"
              checked={bulkScope === 'both'}
              onChange={() => setBulkScope('both')}
            />
            Both
          </label>
          <input
            type="datetime-local"
            className="form-control website-list-time"
            min={toDatetimeLocalValue(new Date())}
            value={bulkEndAt}
            onChange={(e) => setBulkEndAt(e.target.value)}
            aria-label="Maintenance end time for selected websites"
          />
          <div className="website-list-actions">
            <button
              type="button"
              className="website-list-start"
              disabled={!selectedIds.length || saving}
              onClick={() => applyBulkMaintenance(true)}
            >
              {saving ? 'Saving...' : 'Start maintenance'}
            </button>
            <button
              type="button"
              className="website-list-off"
              disabled={!selectedIds.length || saving}
              onClick={() => applyBulkMaintenance(false)}
            >
              Turn off
            </button>
          </div>
        </div>
        <small className="website-list-hint">
          Backend is the agent panel. One end time is applied to every checked website.
        </small>
      </div>
      <table className="table01 margin-table" style={{ marginTop: "5px" }}>
        <thead>
          <tr className="light-grey-bg">
            <th className="website-list-check-col">
              <input
                ref={selectAllRef}
                type="checkbox"
                className="form-check-input website-list-check"
                checked={allFilteredSelected}
                onChange={toggleAllFiltered}
                aria-label="Select all shown websites"
                disabled={!filteredWebsites.length}
              />
            </th>
            <th>Title</th>
            <th>Domain</th>
            <th>Favicon</th>
            <th>Logo</th>
            <th>Login Image</th>
            <th>Mobile Login Image</th>
            <th>Status</th>
            <th>Is Maintenance</th>
            <th>AG Maintenance</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody id="matches-table">
          {filteredWebsites.length > 0 ? filteredWebsites.map((item: WebSiteList, i: any) => {
            const statusOn = !!(item.isMaintenance || item.isAgMaintenance)
            return (
              <tr key={(item as any)._id || i}>
                <td className="website-list-check-col">
                  <input
                    type="checkbox"
                    className="form-check-input website-list-check"
                    checked={selectedIds.includes(item._id)}
                    onChange={() => toggleRow(item._id)}
                    aria-label={`Select ${item.title || item.domain}`}
                  />
                </td>

                {/* Title */}
                <td style={{ padding: "8px 10px", fontWeight: 500 }}>
                  {item.title}
                </td>

                {/* Domain */}
                <td style={{ width: "15%", padding: "8px 10px" }}>
                  <a
                    href={`https://${item.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#1a56db", textDecoration: "underline", fontSize: "13px" }}
                  >
                    {item.domain}
                  </a>
                  {(item.alternativeDomains || []).length > 0 && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#6c757d" }}>
                      {(item.alternativeDomains || []).map((host) => (
                        <div key={host}>
                          <a href={`https://${host}`} target="_blank" rel="noreferrer" style={{ color: "#6c757d", textDecoration: "underline" }}>{host}</a>
                        </div>
                      ))}
                    </div>
                  )}
                </td>

                {/* Favicon */}
                <td style={{ width: "8%", padding: "8px 10px" }}>
                  <a href={getImageUrl(item.favicon)} target="_blank" rel="noreferrer">
                    <img src={getImageUrl(item.favicon)} alt="Favicon" style={{ height: "36px", width: "36px", objectFit: "contain", borderRadius: "4px", border: "1px solid #e0e0e0" }} />
                  </a>
                </td>

                {/* Logo */}
                <td style={{ width: "10%", padding: "8px 10px" }}>
                  <a href={getImageUrl(item.logo)} target="_blank" rel="noreferrer">
                    <img src={getImageUrl(item.logo)} alt="Logo" style={{ height: "36px", maxWidth: "90px", objectFit: "contain", borderRadius: "4px", border: "1px solid #e0e0e0" }} />
                  </a>
                </td>

                {/* Login Image */}
                <td style={{ width: "10%", padding: "8px 10px" }}>
                  <a href={getImageUrl(item.loginImage)} target="_blank" rel="noreferrer">
                    <img src={getImageUrl(item.loginImage)} alt="Login" style={{ height: "36px", maxWidth: "90px", objectFit: "cover", borderRadius: "4px", border: "1px solid #e0e0e0" }} />
                  </a>
                </td>

                {/* Mobile Login Image */}
                <td style={{ width: "10%", padding: "8px 10px" }}>
                  <a href={getImageUrl(item.mobileLoginImage)} target="_blank" rel="noreferrer">
                    <img src={getImageUrl(item.mobileLoginImage)} alt="Mobile Login" style={{ height: "36px", maxWidth: "90px", objectFit: "cover", borderRadius: "4px", border: "1px solid #e0e0e0" }} />
                  </a>
                </td>

                {/* Status */}
                <td style={{ width: "10%", padding: "8px 10px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: statusOn ? "#fdecea" : "#e6f9f0",
                      color: statusOn ? "#b71c1c" : "#1a7a4a",
                      border: `1px solid ${statusOn ? "#f5c6c0" : "#b7e4ce"}`,
                    }}
                  >
                    {item.isMaintenance && item.isAgMaintenance
                      ? "FE + AG"
                      : item.isAgMaintenance
                        ? "AG Maintenance"
                        : item.isMaintenance
                          ? "Maintenance"
                          : "Live"}
                  </span>
                </td>
                {renderToggleCell(item, 'frontend')}
                {renderToggleCell(item, 'ag')}

                {/* Action */}
                <td style={{ padding: "8px 10px" }}>
                  <button
                    onMouseEnter={() => handleMouseEnter(item._id)}
                    onMouseLeave={() => handleMouseLeave(item._id)}
                    style={styleObjectBlackButton(DD?.colorSchema, hoverId.isHover === item._id)}
                    onClick={() => editClick(item)}
                    className="btn_black"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            )
          }) : (
            <tr>
              <td colSpan={11} style={{ textAlign: "center", padding: "40px 0", color: "#9e9e9e", fontSize: "15px" }}>
                {search.trim() ? 'No websites match your search' : 'No websites found'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={`popup-wrp fade ${maintenanceModal.open ? 'popup-show' : ''}`}>
        <div className="pop">
          <div className="pop-content maintenance-time-modal" style={{ padding: "7px" }}>
            <div className="pop-head" style={{ padding: "8px 6px" }}>
              <h5>
                {modalIsAg
                  ? (modalActive ? 'Update AG maintenance time' : 'Set AG maintenance time')
                  : (modalActive ? 'Update maintenance time' : 'Set maintenance time')}
              </h5>
              <div className="pop-close" onClick={closeMaintenanceModal}></div>
            </div>
            <div className="pop-body">
              <p>
                {modalIsAg
                  ? 'The agent panel stays under maintenance until this time. Agents will see a countdown and it will open automatically when it ends. Owners can still log in to turn it off.'
                  : 'The site stays under maintenance until this time. The frontend will show a countdown and open automatically when it ends.'}
              </p>
              <label htmlFor="maintenanceEndAt">{modalIsAg ? 'AG maintenance ends at' : 'Maintenance ends at'}</label>
              <input
                id="maintenanceEndAt"
                type="datetime-local"
                className="form-control"
                min={toDatetimeLocalValue(new Date())}
                value={maintenanceModal.endAt}
                onChange={(e) => setMaintenanceModal((prev) => ({ ...prev, endAt: e.target.value }))}
              />
            </div>
            <ul className="btn-wrap break" style={{ padding: "0", gap: "0" }}>
              <li>
                <a className="btn" style={{ padding: "3px 7px" }} onClick={closeMaintenanceModal}>Cancel</a>
              </li>
              <li>
                <a className="btn btn-send" style={{ padding: "3px 7px" }} onClick={confirmMaintenance}>
                  {saving ? 'Saving...' : (modalActive ? 'Save Time' : (modalIsAg ? 'Start AG Maintenance' : 'Start Maintenance'))}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
}

export default ManageWebsites
